

import { LoadingSpinner } from "@/components/LoadingSpinner/LoadingSpinner";
import { IconMail } from "@tabler/icons-react";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { SentMailEntry } from "./SentMailEntry";
import { PanelEmptyState } from "./PanelEmptyState";
import { EmailSentDTO } from "@/types/EmailsSentDTO";
import { getEmailSentDTOsByEvent } from "@/lib/api/handlers/EmailSentHandling";
import { Link } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { PEmailSent } from "@/components/PEmailSent/PEmailSent";
import { SentMailsDetailsProvider } from "@/providers/SentMailsDetailsProvider";

type SentMailsListProps = {
  guestId: string;
};

export const SentMailsList: React.FC<SentMailsListProps> = ({ guestId }) => {
  const t = useTranslations("guestDetails");
  const t3 = useTranslations("videoMessages");
  const tErrors = useTranslations("errors");
  const params = useParams();
  const eventId = params.eventId as string;

  const [emails, setEmails] = useState<EmailSentDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [previewEmailId, setPreviewEmailId] = useState<string | null>(null);
  const fetched = useRef(false);

  const fetchEmails = useCallback(async () => {
    if (!guestId || !eventId || fetched.current) return;
    // Claimed BEFORE the await so a double mount cannot double-fetch; released
    // again on failure (below), because a one-shot guard over a failed read
    // means the only way back is closing and reopening the drawer.
    fetched.current = true;
    setLoading(true);
    setErrorMessage(null);

    try {
      const result = await getEmailSentDTOsByEvent(
        eventId,
        "emailSentId,mailSubject,templateName,recipientMail,sentAt,emailStatus,senderMail,sendName,"
        // The guest-detail list had drifted behind the sent-mails table: the
        // deferral marker, the skip reason and the attachment warning were all
        // fetched there and not here, so the same row told two different stories
        // depending on which screen you opened.
        + "softBounces,skipReason,attachmentsDegraded,"
        // Audit r2 B1-8: the guest's arrival notices (STAFF_NOTICE — a mail
        // to a staff contact ABOUT this guest) match the guestId filter too;
        // the row needs its class to say so. (The list lane answers no
        // contact label — `notificationContactLabel` is not an allowed field.)
        + "sendClass",
        [{ key: "guestId", operator: "=", value: guestId }],
        "",
        "sentAt",
        "desc",
        0,
        50,
      );

      if (result && !("isServerError" in result)) {
        setEmails(result);
      } else {
        // A failed read is NOT an empty mail history. `getEmailSentDTOsByEvent`
        // resolves with `{isServerError: true}` rather than throwing, so the
        // catch below can never fire and this branch used to turn every server
        // failure into `[]` — the drawer then rendered "no emails sent" for a
        // guest who had received twelve. The sent-mails page showed a real
        // error for the very same request; this door stayed quiet.
        setErrorMessage(tErrors("somethingWentWrong"));
        setEmails([]);
        fetched.current = false;
      }
    } catch {
      setErrorMessage(tErrors("somethingWentWrong"));
      fetched.current = false;
    } finally {
      setLoading(false);
    }
  }, [guestId, eventId]);

  useEffect(() => {
    fetchEmails();
  }, [fetchEmails]);

  return (
    <div className="flex flex-col h-full overflow-y-auto justify-between">
      {loading ? (
        <LoadingSpinner />
      ) : errorMessage ? (
        <div className="p-4 text-center" role="alert">
          <p className="font-error">{errorMessage}</p>
          <button
            type="button"
            className="body-2-bold underline mt-2"
            onClick={() => fetchEmails()}
          >
            {tErrors("retry")}
          </button>
        </div>
      ) : emails.length === 0 ? (
        <PanelEmptyState riv="MailEmpty" title={t3("noEmailsSent")} />
      ) : (
        <ul className="flex flex-col space-y-3 mt-3">
          {emails.map((email) => (
            <SentMailEntry
              email={email}
              key={email.emailSentId}
              onPreview={setPreviewEmailId}
            />
          ))}
        </ul>
      )}
      <div className="w-full flex justify-end items-end mb-2">
        <Link
          to={`/event/${eventId}/messaging/sent-mails`}
          target="_blank"
          rel="noopener noreferrer"
          className="rounded-full p-3 border outline-tertiary"
        >
          <IconMail size={20} className="font-secondary" />
        </Link>
      </div>
      <SentMailsDetailsProvider eventId={eventId}>
        <AnimatePresence>
          {previewEmailId && (
            <PEmailSent
              emailSentId={previewEmailId}
              onClose={() => setPreviewEmailId(null)}
            />
          )}
        </AnimatePresence>
      </SentMailsDetailsProvider>
    </div>
  );
};
